import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
// Components
import Button from "../components/Button";
import Tab from "../components/Tab";
import Checkbox from "../components/Checkbox";
import EuropeanForm from "../components/userForms/EuropeanForm";
import { useSnackBar } from "../components/snackBar/SnackBarContext";
import AutoCopyInfoBanner from "components/popups/AutoCopyInfoBanner";
import { userFieldRefs, participantFieldRefs } from "../refs/refStore";
// Api Services
import {
  submitRegisterDetails,
  updateRegisterDetails,
  getUserWithEmail,
  sendInviteLink,
} from "../api/apiService";
import { initialFormData } from "../components/userForms/initialState";
import { validationRules } from "../components/europeanSections/europValidationRules";
import {
  validateField,
  validateForm,
  validateFlightForm,
} from "../components/userForms/validators";
import {
  transformPayload,
  reconstructFormData,
  transformPayloadOfParticipant,
  reconstructFormDataOfParticipant,
} from "../components/userForms/transformPayload";
// Assets
import Info from "../assets/icons/ic_info.svg";

export default function EuropeanHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackBar();
  const topRef = useRef(null);
  const submitRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeView, setActiveView] = useState("my-info"); // toggle state
  const [participantTabs, setParticipantTabs] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [allParticipantsData, setAllParticipantsData] = useState([]);
  const [isSubmitClicked, setSubmitClicked] = useState(false);
  const [isDeleteClicked, setDeleteClicked] = useState(false);
  const [agreeError, setAgreeError] = useState("");
  const [insuranceConsentError, setInsuranceConsentError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [email, setEmail] = useState("");
  const [isNewUser, setNewUser] = useState(false);
  const [isLoggedIn, setLoggedIn] = useState(false);
  // user
  const [outboundFlights, setOutboundFlights] = useState([]);
  const [inboundFlights, setInboundFlights] = useState([]);
  // participants
  const [participantOutboundFlights, setParticipantOutboundFlights] = useState(
    [],
  );
  const [participantInboundFlights, setParticipantInboundFlights] = useState(
    [],
  );
  const [chooseAddParticipant, setChooseAddParticipant] = useState("");
  const [showChoices, setShowChoices] = useState(false);
  const [tripName, setTripName] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [isInvited, setIsInvited] = useState();
  const [autoCopiedInfo, setAutoCopiedInfo] = useState(false);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [editedField, setEditedField] = useState(null);
  const [editedScope, setEditedScope] = useState("");
  const [inviteFormData, setInviteFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    tripName: sessionStorage.getItem("tripName"),
    senderName: formData.firstName,
    senderEmailId: formData.email,
  });

  useEffect(() => {
    const trip = sessionStorage.getItem("tripName");
    setTripName(trip);
  }, []);

  useEffect(() => {
    setInviteFormData({
      firstName: "",
      lastName: "",
      email: "",
      tripName: sessionStorage.getItem("tripName"),
      senderName: formData.firstName,
      senderEmailId: formData.email,
    });
  }, [formData.firstName, formData.email]);

  useEffect(() => {
    const userType = localStorage.getItem("userType");
    if (userType && userType !== "undefined") setShowPopup(false);
    else setShowPopup(true);
  }, []);

  useEffect(() => {
    if (activeView === "my-info" && topRef.current) {
      // Small delay to ensure rendering
      setTimeout(() => {
        scrollContainerRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    }
  }, [activeView]);

  useEffect(() => {
    if (location.state?.infoMessage) {
      setAutoCopiedInfo(location.state.infoMessage);
    }
  }, [location.state]);

  const handleChange = (e) => {
    // Detect real user interaction
    if (e.isTrusted || e.fromUser) {
      setHasUserEdited(true);
      setAutoSaveEnabled(true); // enable autosave ONLY after first real edit
      setEditedField(e.target.name); // track which field changed
      setEditedScope("user");
    }

    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    const error = validateField(name, value, validationRules(formData));
    if (error != null) setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const sectionRefs = {
    section1: useRef(null),
    section2: useRef(null),
    section3: useRef(null),
    section4: useRef(null),
    section5: useRef(null),
    section6: useRef(null),
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getUserWithEmail(email, tripName);
        setIsInvited(result.personalInfoDTO.childInvite);
        if (
          result.personalInfoDTO.childInvite === "yes" ||
          result.personalInfoDTO.childInvite === "no"
        )
          setShowPopup(false);
        const isInvited = result.personalInfoDTO.childInvite;
        if (isInvited === "yes" || isInvited === "no") {
          localStorage.setItem("userType", isInvited);
          localStorage.setItem("isInvited", isInvited);
          handleChange({ target: { name: "userType", value: isInvited } });
        }

        const reconstructedFormData = reconstructFormData(result);
        setFormData(reconstructedFormData);
        setIsInitialLoadDone(true);
        setHasUserEdited(false); // no auto-save until user manually edits
        setAutoSaveEnabled(false); // stop autosave until user interacts

        setChooseAddParticipant(
          reconstructedFormData.participants.length === 0 ? "no" : "yes",
        );
        let index = 0;
        for (const participant of reconstructedFormData.participants) {
          const newParticipant = reconstructFormDataOfParticipant(participant);
          setAllParticipantsData((prev) => [
            ...prev,
            {
              id: Date.now(),
              formData: newParticipant,
            },
          ]);

          setParticipantOutboundFlights(newParticipant.outboundFlights);
          setParticipantInboundFlights(newParticipant.inboundFlights);

          const newTabId = `participant-${index + 1}`;
          setParticipantTabs((prev) => [...prev, newTabId]);
          index++;
        }
        setFormErrors({});
        setOutboundFlights(reconstructedFormData.outboundFlights);
        setInboundFlights(reconstructedFormData.inboundFlights);
      } catch (err) {
        console.error(err);
      }
    };

    if (!isNewUser && isLoggedIn && email) {
      fetchData();
    } else {
      setFormData({ email: email });
      setIsInvited("");
      setAllParticipantsData([]);
      setInboundFlights([]);
      setOutboundFlights([]);
      setParticipantOutboundFlights([]);
      setParticipantInboundFlights([]);
      setParticipantTabs([]);
    }
  }, [isNewUser, isLoggedIn, email, tripName]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const email = localStorage.getItem("email");
      setEmail(email);
      const isNewUser =
        localStorage.getItem("isNewUser")?.toLowerCase() === "true";
      const isLoggedIn =
        localStorage.getItem("isLoggedIn")?.toLowerCase() === "true";
      if (isLoggedIn) setLoggedIn(isLoggedIn);
      if (isNewUser) setNewUser(isNewUser);
    }
  });

  useEffect(() => {
    if (isDeleteClicked || isSubmitClicked) handleSubmit();
  }, [isSubmitClicked, allParticipantsData]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    const handleScroll = () => {
      const sectionIds = Object.keys(sectionRefs);
      let minDistance = Number.POSITIVE_INFINITY;

      sectionIds.forEach((id) => {
        const el = sectionRefs[id].current;
        if (el) {
          const rect = el.getBoundingClientRect();
          const offsetTop =
            rect.top - scrollContainer.getBoundingClientRect().top;

          const distance = Math.abs(offsetTop - 60); // Adjust for header height
          if (distance < minDistance) {
            minDistance = distance;
          }
        }
      });
    };

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });

      setTimeout(() => {
        handleScroll(); // trigger once after load
      }, 200);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  });

  const runValidation = (shouldValidateChoices) => {
    let { isValid, errors } = validateForm(formData, validationRules(formData));
    const inBoundErrors = validateFlightForm(inboundFlights);
    const outBoundErrors = validateFlightForm(outboundFlights);

    setFormErrors({
      ...errors,
      inboundFlights: inBoundErrors.errors,
      outboundFlights: outBoundErrors.errors,
    });
    let isParticipantError = false;

    if (
      !isValid ||
      !inBoundErrors.isValid ||
      !outBoundErrors.isValid ||
      isParticipantError
    ) {
      showSnackbar(
        "error",
        (!isValid || !inBoundErrors.isValid || !outBoundErrors.isValid) &&
          isParticipantError
          ? "Please complete all mandatory fields in both the primary and second traveler sections."
          : isParticipantError
            ? "Please complete all mandatory fields in the second traveler section."
            : "Please complete all mandatory fields in the primary traveler section.",
      );

      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setDeleteClicked(false);
    setSubmitClicked(false);
    // Run validation first
    const { isValid, errors } = validateForm(
      formData,
      validationRules(formData),
    );

    // Update formErrors state immediately
    setFormErrors(errors);

    // If invalid, scroll to the first field with error
    if (!isValid) {
      setActiveView("my-info");
      setActiveIndex(-1);

      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const fieldElement = document.querySelector(
          `[name="${firstErrorField}"]`,
        );

        if (fieldElement) {
          // If element is inside a custom wrapper (like <label> or <div>), scroll that instead

          setTimeout(() => {
            const field = document.querySelector(`[name="${firstErrorField}"]`);
            field?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 100); //

          // Skip focus for checkboxes and radios (avoid console warning)
          if (
            fieldElement.type !== "checkbox" &&
            fieldElement.type !== "radio"
          ) {
            fieldElement.focus({ preventScroll: true });
          }
        }
      }
      showSnackbar("error", "Please complete all mandatory fields.");
      return; // stop execution
    }

    // Continue your other validations...
    if (!runValidation(true)) return;

    const isDuplicate = allParticipantsData.some(
      (participant) => formData.email === participant.formData.email,
    );

    if (isDuplicate) {
      showSnackbar(
        "error",
        "Secondary traveler email is same as Primary traveler",
      );
      return;
    }

    if (
      showPopup &&
      formData.userType === "yes" &&
      (formData.isVerified === undefined || !formData.isVerified)
    ) {
      setFormErrors((prev) => ({
        ...prev,
        userType: "It is mandatory to verify the email if yes selected",
      }));
      showSnackbar(
        "error",
        "Please complete all mandatory fields in the primary traveler section.",
      );
      return;
    }

    if (!formData.insuranceConsent) {
      setInsuranceConsentError(
        "You must confirm your responsibility for travel insurance.",
      );
      return;
    }

    if (!formData.termsConditions) {
      setAgreeError("You must agree to the Terms and Conditions.");
      return;
    }

    const files = [];
    const payload = transformPayload(
      formData,
      outboundFlights,
      inboundFlights,
      tripName,
    );
    console.log(payload, "Transform Payload");

    const renameFile = (originalFile, newName) => {
      if (!originalFile) {
        console.error("renameFile: originalFile is undefined");
        return null;
      }
      return new File([originalFile], newName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
      });
    };

    files.push(
      renameFile(
        payload.file,
        `${payload.personalInfoDTO.lastName}_${payload.personalInfoDTO.firstName}.png`,
      ),
    );

    payload.participants = [];

    for (const participant of allParticipantsData) {
      participant.formData.isKnownUser = formData.isKnownUser;
      const participantData = transformPayloadOfParticipant(
        participant.formData,
        participantOutboundFlights,
        participantInboundFlights,
      );
      payload.participants.push(participantData);
      files.push(
        renameFile(
          participantData.file,
          `${participantData.personalInfoDTO.lastName}_${participantData.personalInfoDTO.firstName}.png`,
        ),
      );
    }
    console.log(payload, "post Payload");

    try {
      const response = !isNewUser
        ? await updateRegisterDetails(
            payload,
            tripName,
            payload.personalInfoDTO.personalId,
            files,
          )
        : await submitRegisterDetails(payload, tripName, files);

      if (
        inviteFormData.firstName &&
        inviteFormData.email &&
        formData.isKnownUser === "no"
      ) {
        await sendInviteLink(inviteFormData, tripName);
      }

      if (!isDeleteClicked) {
        showSnackbar(
          "success",
          `${!isNewUser ? "Updated" : "Submitted"} successfully!`,
        );
        console.log("Server Response:", response);
        localStorage.clear();
        navigate("/logout");
      } else {
        showSnackbar("success", `Participant Removed successfully!`);
      }
    } catch (error) {
      console.error("Submit Error:", error);
      showSnackbar("error", error.message);
    }
  };

  const handleSaveAsDraft = async () => {
    const files = [];
    const payload = transformPayload(
      formData,
      outboundFlights,
      inboundFlights,
      tripName,
    );
    payload.personalInfoDTO.status = "DRAFT";
    // console.log(payload, "Draft Payload");

    const renameFile = (originalFile, newName) => {
      if (!originalFile) {
        console.error("renameFile: originalFile is undefined");
        return null;
      }
      return new File([originalFile], newName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
      });
    };

    files.push(
      renameFile(
        payload.file,
        `${payload.personalInfoDTO.lastName}_${payload.personalInfoDTO.firstName}.png`,
      ),
    );

    payload.participants = [];

    for (const participant of allParticipantsData) {
      participant.formData.isKnownUser = formData.isKnownUser;
      const participantData = transformPayloadOfParticipant(
        participant.formData,
        participantOutboundFlights,
        participantInboundFlights,
      );
      payload.participants.push(participantData);
      files.push(
        renameFile(
          participantData.file,
          `${participantData.personalInfoDTO.lastName}_${participantData.personalInfoDTO.firstName}.png`,
        ),
      );
    }

    try {
      const response = !isNewUser
        ? await updateRegisterDetails(
            payload,
            tripName,
            payload.personalInfoDTO.personalId,
            files,
          )
        : await submitRegisterDetails(payload, tripName, files);
      // Set isNewUser = false in localStorage
      localStorage.setItem("isNewUser", "false");
      setNewUser(false); // also update your state immediately

      const result = await getUserWithEmail(email, tripName);
      setIsInvited(result.personalInfoDTO.childInvite);
      if (
        result.personalInfoDTO.childInvite === "yes" ||
        result.personalInfoDTO.childInvite === "no"
      )
        setShowPopup(false);
      const isInvited = result.personalInfoDTO.childInvite;
      if (isInvited === "yes" || isInvited === "no") {
        localStorage.setItem("userType", isInvited);
        localStorage.setItem("isInvited", isInvited);
        handleChange({ target: { name: "userType", value: isInvited } });
      }

      const reconstructedFormData = reconstructFormData(result);
      setFormData(reconstructedFormData);
      setIsInitialLoadDone(true);
      setHasUserEdited(false); // no auto-save until user manually edits
      setAutoSaveEnabled(false); // stop autosave until user interacts

      if (response) {
        showSnackbar("success", "Form saved as draft successfully!");
      } else {
        showSnackbar("error", "Failed to save draft. Please try again.");
      }
    } catch (error) {
      console.error("Submit Error:", error);
      showSnackbar("error", error.message);
    }
  };

  useEffect(() => {
    const shouldShowChoices = () => {
      if (activeIndex === allParticipantsData.length) return true;
      if (formData.isKnownUser === "yes") return false;
      return true;
    };

    setShowChoices(shouldShowChoices);
  }, [
    formData.isKnownUser,
    allParticipantsData,
    activeIndex,
    chooseAddParticipant,
    activeView,
  ]);

  const handleCategoryChangeFromChild = (categoryValue) => {
    if (categoryValue === "Single") {
      setShowChoices(true);
    } else {
      setShowChoices(false);
    }
  };

  useEffect(() => {
    if (!editedField || !editedScope) return;

    let input = null;

    if (editedScope === "user") {
      input = userFieldRefs[editedField]?.current;
    }

    if (editedScope === "participant") {
      input = participantFieldRefs[editedField]?.current;
    }

    if (!input) return;
  }, [editedField, editedScope]);

  const onSectionExit = (sectionId) => {
    // if (!isInitialLoadDone) return;
    // if (!autoSaveEnabled) return;
    // if (!hasUserEdited) return;
    // const fields = sectionFieldMap[sectionId];
    // if (!fields) return;
    // // Check if user filled ANY field in this section
    // const isAnyFieldFilled = fields.some((field) => {
    //   const value = formData[field];
    //   return value !== undefined && value !== null && value !== "";
    // });
    // if (isAnyFieldFilled) {
    //   console.log(`Auto-saving Draft - User edited something in ${sectionId}`);
    //   handleSaveAsDraft();
    // }
  };

  useEffect(() => {
    if (
      tripName.toLowerCase().includes("europ") &&
      !formData.conferenceMissionUnion
    ) {
      setFormData((prev) => ({
        ...prev,
        conferenceMissionUnion: "Southern New England Conference",
      }));
    }
  }, [formData.conferenceMissionUnion, tripName]);

  return (
    <div className="">
      <div className="bg-custom-image bg-cover bg-center h-[100dvh] w-full">
        <div className="pb-4 h-[100dvh] md:h-screen mx-auto bg-white bg-opacity-95 scrollbar-thin overflow-y-auto">
          <div className="block sm:flex h-full">
            <div className="h-full max-w-screen-md mx-auto">
              <div className="border-r border-l border-inputBorder">
                <div className="sticky top-0 z-20 bg-white border-b shadow-sm">
                  <Tab
                    activeView={activeView}
                    onToggle={setActiveView}
                    setActiveIndex={setActiveIndex}
                    participantTabs={participantTabs}
                    setLoggedIn={setLoggedIn}
                    handleSaveAsDraft={handleSaveAsDraft}
                    formData={formData}
                  />

                  {/* For primary user */}
                  <AutoCopyInfoBanner
                    message={autoCopiedInfo}
                    show={activeView === "my-info" && !!autoCopiedInfo}
                    onClose={() => setAutoCopiedInfo("")}
                  />
                </div>

                <div
                  ref={scrollContainerRef}
                  className="overflow-y-auto border-b h-full max-h-[90%] bg-white bg-opacity-0"
                >
                  <div
                    ref={submitRef}
                    className={`${
                      activeView === "my-info" ? "block" : "hidden"
                    }`}
                  >
                    <EuropeanForm
                      showPopup={showPopup}
                      sectionRefs={sectionRefs}
                      handleChange={handleChange}
                      formErrors={formErrors}
                      setOutboundFlights={setOutboundFlights}
                      setInboundFlights={setInboundFlights}
                      outboundFlights={outboundFlights}
                      inboundFlights={inboundFlights}
                      formData={formData}
                      setFormData={setFormData}
                      onChoiceChange={(selected) => {
                        setFormData((prev) => ({
                          ...prev,
                          travelerChoice: selected,
                        }));
                      }}
                      setFormErrors={setFormErrors}
                      isInvited={isInvited}
                      onKnownUserChange={(value) => {
                        setFormData((prev) => ({
                          ...prev,
                          isKnownUser: value,
                        }));
                      }}
                      onCategoryChange={handleCategoryChangeFromChild} // ✅ passed here
                      allParticipantsData={allParticipantsData}
                      setParticipantOutboundFlights={
                        setParticipantOutboundFlights
                      }
                      setParticipantInboundFlights={
                        setParticipantInboundFlights
                      }
                      fieldRefs={userFieldRefs}
                      inviteFormData={inviteFormData}
                      setInviteFormData={setInviteFormData}
                      onSectionExit={onSectionExit}
                    />
                  </div>
                </div>

                {/* Terms and Conditions */}
                {showChoices && (
                  <div className="px-5 py-4 gap-1">
                    <div className="pb-4">
                      <Checkbox
                        name="insuranceConsent"
                        checked={formData.insuranceConsent}
                        onChange={(e) => {
                          const isChecked = e.target.checked;

                          handleChange({
                            target: {
                              name: "insuranceConsent",
                              value: isChecked,
                            },
                          });
                        }}
                        label={
                          <>
                            I consent that I am responsible for purchasing my
                            travel insurance, and I accept full responsibility
                            for any related expenses. I release{" "}
                            <span className="font-semibold">
                              BiblicalTouring.com
                            </span>{" "}
                            from any liability.
                          </>
                        }
                        items="items-start"
                        marginTop="mt-1"
                        required
                      />

                      {insuranceConsentError && (
                        <div className="flex gap-1 w-fit text-[10px] bg-red-100 text-red-600 mt-1 rounded p-[3px]">
                          <img src={Info} alt="info" />
                          {insuranceConsentError}
                        </div>
                      )}
                    </div>

                    <Checkbox
                      name="terms"
                      checked={formData.termsConditions}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        handleChange({
                          target: {
                            name: "termsConditions",
                            value: isChecked,
                          },
                        });
                      }}
                      label={
                        <>
                          I have read and agree to the tour{" "}
                          <Link
                            to="https://biblicaltouring.com/terms-condition/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <button
                              type="button"
                              className="text-buttonColor font-medium hover:underline"
                            >
                              Terms and Conditions
                            </button>
                          </Link>
                        </>
                      }
                      required
                    />
                    {agreeError && (
                      <div className="flex gap-1 w-fit text-[10px] bg-red-100 text-red-600 mt-1 rounded p-[3px]">
                        <img src={Info} alt="info" />
                        {agreeError}
                      </div>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <div
                  className={`px-4 py-3 border-b flex flex-col sm:flex-row items-center gap-3 sm:gap-0 ${
                    isNewUser ? "sm:justify-end" : "sm:justify-center"
                  }`}
                >
                  {/* Submit Button */}
                  <div className="w-full sm:w-auto">
                    <Button
                      disabled={!formData.termsConditions}
                      className={`w-full sm:w-36 h-12 text-white ${
                        formData.termsConditions
                          ? "bg-green-600"
                          : "bg-buttonColor"
                      }`}
                      onClick={() => {
                        setSubmitClicked(true);
                      }}
                      text={!isNewUser ? "UPDATE" : "SUBMIT"}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
